import {
  Box,
  Flex,
  Heading,
  Icon,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { Actions, Features, useAnalyticsContext } from "@metriport/shared-internal";
import { useCallback, useEffect, useState } from "react";
import { FaExternalLinkAlt, FaWifi } from "react-icons/fa";
import { useZorm } from "react-zorm";
import {
  getSettings,
  Settings,
  SettingsUpdate,
  settingsUpdateSchema,
  updateSettings,
} from "../../api/settings";
import { getWebhookStatus, WebhookStatus } from "../../api/webhook-status";
import { capture } from "../../shared/capture";
import { isAxiosNetworkError, retryWithExponentialBackoff } from "../../shared/retry";
import { useAppContext } from "../contexts/app";
import Dialog from "../dialog";
import Key from "../key";
import { Button } from "../medical/shared-components/button";
import useMetriportToast from "../medical/shared-logic/useMetriportToast";
import Constants from "../shared/constants";
import { Label } from "../shared/Label";
import { Link } from "../shared/Link";
import { sleep } from "../shared/util";
import WebhookRequests from "./webhook-requests";
import WebhookTester from "./webhook-tester";

const DEFAULT_WEBHOOK_URL = "";
const DEFAULT_WEBHOOK_KEY = "";
const DEFAULT_WEBHOOK_STATUS = "--";

const defaultSettings = {
  id: "",
  webhookUrl: DEFAULT_WEBHOOK_URL,
  webhookKey: DEFAULT_WEBHOOK_KEY,
};

const defaultWebhookStatus = {
  webhookEnabled: false,
  webhookStatusDetail: DEFAULT_WEBHOOK_STATUS,
  webhookRequestsProcessing: -1,
  webhookRequestsFailed: -1,
};

export default function Webhook() {
  const { state } = useAppContext();
  const [settings, setSettings] = useState<Settings | null>(defaultSettings);
  const [webhookStatus, setWebhookStatus] = useState<WebhookStatus>(defaultWebhookStatus);
  const [errorTitle, setErrorTitle] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useMetriportToast();
  const [isLargerThan1000] = useMediaQuery("(min-width: 1000px)");
  const [showKeys, setShowKeys] = useState(false);
  const Analytics = useAnalyticsContext();

  const isWebhookConfigured = settings?.webhookUrl && settings?.webhookKey;

  const zorm = useZorm("webhook", settingsUpdateSchema, {
    onValidSubmit(e) {
      e.preventDefault();
      update(e.data);
    },
  });

  async function update({ webhookUrl }: SettingsUpdate) {
    Analytics.emit(Actions.test, Features.webhookUrl);
    try {
      setIsSubmitting(true);
      setShowKeys(false);
      setWebhookStatus({
        ...webhookStatus,
        webhookStatusDetail: DEFAULT_WEBHOOK_STATUS,
      });
      const updatedSettings = await updateSettings({ webhookUrl });
      setSettings({
        ...updatedSettings,
      });
      if (!updatedSettings.webhookUrl) {
        toast.success({ title: "Saved" });
      } else {
        toast.success({ title: "Saved, checking status..." });
        await sleep(3_000);
        const updatedStatus = await getWebhookStatus();
        if (updatedStatus) {
          setWebhookStatus(updatedStatus);
        }
        toast.success({ title: "Status updated" });
      }
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.response?.status >= 400 && err.response?.status <= 499) {
        setErrorTitle("Validation message");
        setErrorMessage(err.response?.detail ?? "Invalid URL");
      } else {
        capture.error(err, {
          extra: { webhookUrl, context: `webhook.update`, captureEnv: capture.getEnvironment() },
        });
        setErrorMessage(Constants.errorDefaultMessage);
      }
    }
    setIsSubmitting(false);
  }

  const loadSettings = useCallback(async () => {
    const apiKeyId = state.keyIds?.apiKeyId;
    if (!apiKeyId) {
      return setSettings(defaultSettings);
    }

    try {
      const localSettingsPromise = () => getSettings();
      const localWebhookStatusPromise = () => getWebhookStatus();

      const [localSettings, localWebhookStatus] = await Promise.all([
        // TODO update to use executeWithNetworkRetries
        retryWithExponentialBackoff(
          localSettingsPromise,
          "webhook.loadSettings",
          isAxiosNetworkError,
          6
        ),
        // TODO update to use executeWithNetworkRetries
        retryWithExponentialBackoff(
          localWebhookStatusPromise,
          "webhook.loadSettings",
          isAxiosNetworkError,
          6
        ),
      ]);

      setSettings(localSettings);
      setWebhookStatus(localWebhookStatus ?? defaultWebhookStatus);
    } catch (err) {
      toast.error({
        title: "Failed to load webhook settings. Please reload the page.",
        duration: 5000,
      });
    }
  }, [setSettings, setWebhookStatus, state.keyIds]);

  useEffect(() => {
    setSettings(null);
    loadSettings();
  }, [loadSettings]);

  const clearErrMessage = () => {
    setErrorMessage(undefined);
    setErrorTitle(undefined);
  };

  async function handleRetryEnd() {
    try {
      await loadSettings();
    } catch (err) {
      capture.error(err, { extra: { context: `webhook.retry.onEnd` } });
      setErrorMessage(Constants.errorDefaultMessage);
      setErrorTitle(undefined);
    } finally {
      setIsSubmitting(false);
    }
  }

  const renderHeader = () => (
    <>
      <Heading size="lg">Webhook</Heading>
      <Text>
        This is your application's URL to receive events from Metriport -{" "}
        <Link href="https://docs.metriport.com/medical-api/getting-started/webhooks" isExternal>
          see guide <Icon as={FaExternalLinkAlt} fontSize="xs" />
        </Link>
        .
      </Text>
    </>
  );

  const renderFormActions = () => (
    <Flex alignItems="center" gap={2} justifyContent="space-between">
      <Heading size="sm">Destination</Heading>
      <Flex alignItems="center" gap={2}>
        <Button
          onClick={() => update({ webhookUrl: settings?.webhookUrl || "" })}
          isLoading={isSubmitting}
          size="sm"
        >
          Save & Ping <Icon as={FaWifi} fontSize="xs" ml={1} />
        </Button>
      </Flex>
    </Flex>
  );

  const renderUrlInput = () => (
    <Flex alignItems="left" direction={isLargerThan1000 ? "row" : "column"}>
      <Stack direction="column" flexGrow={3}>
        <Flex alignItems="left" direction={isLargerThan1000 ? "row" : "column"}>
          <Label>URL:</Label>
          <Input
            type="text"
            name={zorm.fields.webhookUrl()}
            className={zorm.errors.webhookUrl("errored")}
            value={settings?.webhookUrl ?? DEFAULT_WEBHOOK_URL}
            onChange={(e: { target: { value: string } }) => {
              if (!settings) return;
              setSettings({
                ...settings,
                webhookUrl: e.target.value,
              });
            }}
            mt={3}
          />
        </Flex>
        {zorm.errors.webhookUrl(() => (
          <Text>Webhook must be a valid URL</Text>
        ))}
      </Stack>
    </Flex>
  );

  const renderStatusSection = () => (
    <Flex alignItems="left" direction={isLargerThan1000 ? "row" : "column"} flexGrow={3}>
      <Label>Last status:</Label>
      {isLargerThan1000 ? (
        <Input
          variant="filled"
          value={webhookStatus?.webhookStatusDetail ?? DEFAULT_WEBHOOK_STATUS}
          readOnly
          mt={3}
          fontFamily="courier"
        />
      ) : (
        <Textarea
          variant="filled"
          value={webhookStatus?.webhookStatusDetail ?? DEFAULT_WEBHOOK_STATUS}
          readOnly
          mt={3}
          fontFamily="courier"
          noOfLines={3}
        />
      )}
    </Flex>
  );

  const renderWebhookContent = () => (
    <Box mt={5}>
      <form ref={zorm.ref}>
        <Flex direction="column">
          {renderFormActions()}
          {renderUrlInput()}
          <Key value={settings?.webhookKey ?? DEFAULT_WEBHOOK_KEY} show={showKeys} />
          <WebhookRequests
            webhookRequestsProcessing={webhookStatus ? webhookStatus.webhookRequestsProcessing : -1}
            webhookRequestsFailed={webhookStatus ? webhookStatus.webhookRequestsFailed : -1}
            isSubmitting={isSubmitting}
            onRetryStart={() => {
              setIsSubmitting(true);
              setShowKeys(false);
            }}
            onRetryEnd={handleRetryEnd}
            onError={(msg: string) => setErrorMessage(msg)}
          />
          {renderStatusSection()}
        </Flex>
      </form>

      {isWebhookConfigured ? (
        <WebhookTester />
      ) : (
        <Box mt={5}>
          <Text>Configure your webhook settings above to enable testing.</Text>
        </Box>
      )}

      {!!errorMessage && (
        <Dialog show message={errorMessage} title={errorTitle} onClose={clearErrMessage} />
      )}
    </Box>
  );

  return (
    <Box mb={10}>
      <VStack align="stretch">
        {renderHeader()}
        {!settings ? (
          <Spinner size="xl" />
        ) : (
          <VStack align="left">
            {state.keyIds?.apiKeyId ? (
              renderWebhookContent()
            ) : (
              <Text>Need to generate an API key before configuring your webhook.</Text>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
